import PropTypes from 'prop-types';
import classNames from 'classnames';
import React, { useState } from 'react';
import TRANSITION from '@integrations/pint-appstore-commons/src/enums/APP_STATUS_TRANSITION.json';
import AppStatusOption from './AppStatusOption';

const transitionToLabelMap = {
    [TRANSITION.DISABLE]: 'Not enabled for domain',
    [TRANSITION.GROUP]: 'Added for groups',
    [TRANSITION.ALLOW]: 'Allowed for users',
    [TRANSITION.ADD]: 'Added for all users',
};

/**
 * AppStatusChooser component provides a dropdown interface for selecting app installation status
 * @component
 * @param {Object} props - Component props
 * @param {string} props.name - Application name to display in status descriptions
 * @param {string} props.transition - Current status transition value (DISABLE, GROUP, ALLOW, or ADD)
 * @param {Function} props.handleChange - Callback function triggered when status changes
 * @param {boolean} props.hideGroups - When true, hides the group-based installation option
 * @param {boolean} props.disabled - When true, disables status selection (requirements not met)
 * @param {boolean} [props.hideAllowedUsers=false] - When true, hides the "Allowed for users" option
 * @returns {React.ReactElement} A dropdown component for app status selection
 */
function AppStatusChooser({ name, transition, handleChange, hideGroups, disabled, hideAllowedUsers = false }) {
    const [isOpen, setIsOpen] = useState(false);
    const toggleHandler = () => setIsOpen((flag) => !flag);
    const selectOption = (newTransition) => {
        setIsOpen(false);
        handleChange(newTransition);
    };

    return (
        <div className={`${isOpen ? 'ast-app-status-chooser--open ' : ''}ast-app-status-chooser`}>
            <div className="ast-app-status-chooser__title">Status</div>
            <div
                data-tooltip="You must check all the requirements above to enable this."
                className={classNames('ast-app-status-chooser__requirements-tooltip', {
                    'ast-app-status-chooser__requirements-tooltip--disabled': !disabled,
                })}
            >
                <div
                    onClick={toggleHandler}
                    className={classNames('ast-app-status-chooser__button', {
                        'ast-app-status-chooser__button--disabled': disabled,
                    })}
                >
                    {transitionToLabelMap[transition]}
                </div>
            </div>
            <div className="ast-app-status-chooser__dropdown">
                <AppStatusOption
                    value={TRANSITION.DISABLE}
                    isSelected={transition === TRANSITION.DISABLE}
                    onClick={selectOption}
                    label={transitionToLabelMap[TRANSITION.DISABLE]}
                >
                    {name} will not be enabled for your domain users.
                </AppStatusOption>
                {!hideGroups && (
                    <AppStatusOption
                        value={TRANSITION.GROUP}
                        isSelected={transition === TRANSITION.GROUP}
                        onClick={selectOption}
                        label={transitionToLabelMap[TRANSITION.GROUP]}
                    >
                        You will select which groups will have this integration enabled.
                    </AppStatusOption>
                )}
                {!hideAllowedUsers && (
                    <AppStatusOption
                        value={TRANSITION.ALLOW}
                        isSelected={transition === TRANSITION.ALLOW}
                        onClick={selectOption}
                        label={transitionToLabelMap[TRANSITION.ALLOW]}
                    >
                        Users will be able to add this add-on from add-ons page.
                    </AppStatusOption>
                )}
                <AppStatusOption
                    value={TRANSITION.ADD}
                    isSelected={transition === TRANSITION.ADD}
                    onClick={selectOption}
                    label={transitionToLabelMap[TRANSITION.ADD]}
                >
                    Every user will have this add-on installed by default.
                    <br />
                    No user action required.
                </AppStatusOption>
            </div>
        </div>
    );
}
AppStatusChooser.propTypes = {
    name: PropTypes.string.isRequired,
    transition: PropTypes.string.isRequired,
    handleChange: PropTypes.func.isRequired,
    hideGroups: PropTypes.bool.isRequired,
    disabled: PropTypes.bool.isRequired,
    hideAllowedUsers: PropTypes.bool,
};

export default AppStatusChooser;
